import { useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { Shield, Cloud, Globe, Lock } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import BrandLogo from '../../components/ui/BrandLogo';
import { authService } from '../../services/authService';

export default function Login() {
  const navigate = useNavigate();
  const { isAuthenticated, login, verifyMfa } = useAuth();
  const branding = useBranding();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaEmail, setMfaEmail] = useState('');
  const [totpCode, setTotpCode] = useState('');

  if (isAuthenticated) return <Navigate to="/" replace />;

  const btnGradient = `linear-gradient(135deg, ${branding?.secondaryColor || '#e94560'}, ${branding?.primaryColor || '#1a1a2e'})`;
  // Microsoft Entra remains available on every login surface. The backend is
  // authoritative for configuration and returns a safe authentication error if
  // the active domain is not enabled.
  const ssoEnabled = true;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.mfaRequired) {
        setMfaEmail(result.email);
        setMfaStep(true);
      } else {
        // User doesn't have MFA set up yet — redirect to mandatory MFA enrollment
        navigate('/mfa-setup');
        return;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaEmail, totpCode);
      // AuthContext sets authenticated=true, Navigate kicks in
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSso = () => {
    setError('');
    const forcedDomain = import.meta.env.VITE_FORCE_DOMAIN;
    window.location.href = authService.getSsoStartUrl({
      domain: forcedDomain || window.location.hostname,
      returnUrl: `${window.location.origin}/auth/sso/callback`,
    });
  };

  const inputStyle = {
    width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 8,
    padding: '12px 14px', color: C.t1, fontSize: 13, fontFamily: FONTS.mono, outline: 'none',
    transition: 'border-color 0.15s', boxSizing: 'border-box',
  };

  return (
    <div className="sentinel-login-shell" style={{ minHeight: '100vh', display: 'flex', background: C.bg, transition: 'background 0.4s' }}>
      <style>{`
        @media (max-width: 800px) {
          .sentinel-login-shell { flex-direction: column; }
          .sentinel-login-brand { flex: 0 0 auto !important; width: 100%; box-sizing: border-box; padding: 20px 24px !important; border-right: 0 !important; border-bottom: 1px solid ${C.b1}; }
          .sentinel-login-brand-header { margin-bottom: 0 !important; }
          .sentinel-login-brand-details, .sentinel-login-brand-footer { display: none !important; }
          .sentinel-login-form-area { width: 100%; min-width: 0; box-sizing: border-box; padding: 24px !important; align-items: flex-start !important; }
          .sentinel-login-form { width: 100% !important; max-width: 380px; }
        }
      `}</style>
      {/* Left — branding panel */}
      <div className="sentinel-login-brand" style={{
        flex: '0 0 420px', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: '60px 50px', background: C.s1, borderRight: `1px solid ${C.b1}`,
        transition: 'background 0.4s, border-color 0.4s',
      }}>
        <div className="sentinel-login-brand-header" style={{ marginBottom: 40 }}>
          <BrandLogo
            src={branding?.logoUrl}
            alt={branding?.portalName || 'Sentinel Command'}
            letter={branding?.logoLetter || 'S'}
            primaryColor={branding?.primaryColor || '#1a1a2e'}
            secondaryColor={branding?.secondaryColor || '#e94560'}
            width="min(300px, 100%)"
            height={104}
            maxWidth={300}
            maxHeight={104}
            style={{ marginBottom: 16 }}
            fallbackStyle={{ width: 38, height: 38, fontSize: 18 }}
          />
          <div style={{ fontSize: 22, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, letterSpacing: '-0.02em', transition: 'color 0.4s' }}>
            {branding?.portalName || 'Sentinel Command'}
          </div>
        </div>

        <div className="sentinel-login-brand-details" style={{ fontSize: 28, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, lineHeight: 1.3, marginBottom: 16, transition: 'color 0.4s' }}>
          {branding?.tagline || 'Cybersecurity Command Portal'}
        </div>
        {branding?.description && (
          <div className="sentinel-login-brand-details" style={{ fontSize: 13, color: C.t2, fontFamily: FONTS.mono, lineHeight: 1.7, marginBottom: 30, transition: 'color 0.4s' }}>
            {branding.description}
          </div>
        )}

        <div className="sentinel-login-brand-details" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(branding?.features?.length > 0 ? branding.features : [
            'Real-time vulnerability tracking & EPSS scoring',
            'Cloud security posture across Azure & Microsoft 365',
            'Automated attack path analysis',
            'NIST CSF, CIS, SOC 2 compliance mapping',
          ]).map((text, i) => {
            const Icons = [Shield, Cloud, Globe, Lock];
            const Ico = Icons[i % Icons.length];
            return (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ display: 'inline-flex', opacity: 0.5, color: C.t2 }}><Ico size={14} strokeWidth={1.5} /></span>
                <span style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>{text}</span>
              </div>
            );
          })}
        </div>
        <div className="sentinel-login-brand-footer" style={{ marginTop: 'auto', paddingTop: 40, fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>
          {branding?.portalName || 'Security Portal'} v2.0
        </div>
      </div>

      {/* Right — login form */}
      <div className="sentinel-login-form-area" style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, boxSizing: 'border-box', transition: 'background 0.4s' }}>
        <div className="sentinel-login-form" style={{ width: '100%', maxWidth: 380 }}>

          {!mfaStep ? (
            <form onSubmit={handleLogin}>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Welcome</div>
              <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 28 }}>Sign in to continue</div>

              {branding?._loadState === 'error' && (
                <div role="status" style={{ padding: '9px 12px', borderRadius: 8, background: `${C.amb}12`, border: `1px solid ${C.amb}33`, marginBottom: 16, fontSize: 10, color: C.amb, fontFamily: FONTS.mono, lineHeight: 1.5 }}>
                  Portal configuration could not be verified. Microsoft Entra sign-in remains available, but may return a configuration error; email, password, and password recovery are unaffected.
                </div>
              )}

              {error && (
                <div role="alert" style={{ padding: '10px 14px', borderRadius: 8, background: C.redBg, border: `1px solid ${C.red}33`, marginBottom: 16, fontSize: 11, color: C.red, fontFamily: FONTS.mono }}>{error}</div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label htmlFor="login-email" style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Email Address</label>
                <input
                  id="login-email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com" type="email" autoComplete="username" autoFocus required
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <label htmlFor="login-password" style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Password</label>
                  <Link to="/forgot-password" style={{ fontSize: 10, color: C.sky, fontFamily: FONTS.mono, textDecoration: 'none' }}>Forgot password?</Link>
                </div>
                <input id="login-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••••••" type="password" autoComplete="current-password" required style={inputStyle} />
              </div>

              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', cursor: loading ? 'wait' : 'pointer',
                background: loading ? C.t3 : btnGradient,
                color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: FONTS.sans, transition: 'opacity 0.15s',
              }}>{loading ? 'Signing in...' : 'Sign In \u2192'}</button>

              {ssoEnabled && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0' }}>
                    <div style={{ flex: 1, height: 1, background: C.b1 }} />
                    <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em' }}>or</div>
                    <div style={{ flex: 1, height: 1, background: C.b1 }} />
                  </div>
                  <button type="button" onClick={handleSso} style={{
                    width: '100%', padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.b1}`,
                    cursor: 'pointer', background: C.s2, color: C.t1, fontSize: 13, fontWeight: 700,
                    fontFamily: FONTS.sans, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}>
                    <Lock size={15} strokeWidth={1.8} />
                    Sign in with Microsoft Entra ID
                  </button>
                </>
              )}
            </form>
          ) : (
            <form onSubmit={handleMfa}>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Two-Factor Authentication</div>
              <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 28 }}>Enter the 6-digit code from your authenticator app</div>

              {error && (
                <div role="alert" style={{ padding: '10px 14px', borderRadius: 8, background: C.redBg, border: `1px solid ${C.red}33`, marginBottom: 16, fontSize: 11, color: C.red, fontFamily: FONTS.mono }}>{error}</div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label htmlFor="login-totp" style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Verification Code</label>
                <input
                  id="login-totp" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" type="text" autoFocus autoComplete="one-time-code"
                  inputMode="numeric" maxLength={6} required
                  style={{ ...inputStyle, textAlign: 'center', fontSize: 24, fontWeight: 700, letterSpacing: '0.3em' }}
                />
              </div>

              <button type="submit" disabled={loading || totpCode.length !== 6} style={{
                width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', cursor: loading ? 'wait' : 'pointer',
                background: loading || totpCode.length !== 6 ? C.t3 : btnGradient,
                color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: FONTS.sans, transition: 'opacity 0.15s',
              }}>{loading ? 'Verifying...' : 'Verify \u2192'}</button>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <button type="button" onClick={() => { setMfaStep(false); setTotpCode(''); setError(''); }} style={{ fontSize: 11, color: C.sky, fontFamily: FONTS.mono, cursor: 'pointer', background: 'transparent', border: 0, padding: 0 }}>
                  \u2190 Back to sign in
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
